
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { calculateLoadedScore, getMaxScores } from '@/utils/scoreCalculation';

export const useLoadedGuide = (session: Session | null) => {
  const { data: guidance, isLoading } = useQuery({
    queryKey: ['guidance', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      try {
        // Get the user's profile data
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*, assessment_guidance(*)')
          .eq('id', session.user.id)
          .single();
          
        if (profileError) throw profileError;
        
        // Get the user's skills data
        const { data: skills, error: skillsError } = await supabase
          .from('skills')
          .select('*')
          .eq('user_id', session.user.id);
          
        if (skillsError) throw skillsError;
        
        // Return the guidance data
        return {
          profile,
          skills,
          guidance: profile.assessment_guidance?.[0] || null
        };
      } catch (error) {
        console.error('Error fetching guidance:', error);
        return null;
      }
    },
    enabled: !!session?.user?.id,
  });
  
  // Calculate the loaded score based on skills data
  const calculateScore = () => {
    if (!guidance?.skills || guidance.skills.length === 0) {
      return 0;
    }
    
    const scoreComponents = {
      technical: guidance.skills.find(s => s.category === 'Technical Skills')?.current_level * 5 || 0,
      industry: guidance.skills.find(s => s.category === 'Industry Skills')?.current_level * 6 || 0,
      leadership: guidance.skills.find(s => s.category === 'Leadership Skills')?.current_level * 7 || 0,
      communication: guidance.skills.find(s => s.category === 'Core Skills')?.current_level * 5 || 0,
      growth: guidance.skills.find(s => s.category === 'Business Skills')?.current_level * 6 || 0
    };
    
    return calculateLoadedScore(scoreComponents);
  };
  
  // Use calculated score directly instead of trying to access profile.total_score
  const loadedScore = guidance ? calculateScore() : 0;
  const careerStage = guidance?.guidance?.current_level || 'Explorer';
  
  return {
    loadedScore,
    careerStage,
    guidance: guidance?.guidance || null,
    isLoading
  };
};
